import type { ScheduleResult } from "@planerius/contracts/cabinet"

import { formatTimeOfDay, todayInZone } from "../../lib/format"
import { plural } from "../../lib/plural"

/**
 * 5-М2 «Моя неделя» — неделя мастера списком дней, отдельно от разметки и от сети.
 *
 * ПОЧЕМУ СПИСОК, А НЕ СЕТКА ПО ЧАСАМ. На 360 px семь колонок по получасам — это клетки
 * по 6 px: в них не помещается ни время, ни имя, и попасть пальцем нельзя. Мастер же
 * смотрит неделю не чтобы двигать записи (он этого и не может — график ставит
 * администратор), а чтобы ответить себе на два вопроса: когда я работаю и насколько
 * забит день. Оба ответа — это строка.
 *
 * НЕДЕЛЯ СОБИРАЕТСЯ ИЗ НЕСКОЛЬКИХ ОТВЕТОВ, ПО ОДНОМУ НА ФИЛИАЛ. У журнала branchId
 * обязателен и режима «все точки» у него нет и быть не может: у филиалов разные зоны,
 * шаг сетки и часы работы. Мастер при этом работает в двух точках — это обычный случай,
 * и «смена 12–21 · на Гагарина» требует показать обе. Поэтому запросы идут по филиалам,
 * а сшивка по календарным датам происходит здесь.
 *
 * ДЕНЬ СЧИТАЕТСЯ В ЗОНЕ СВОЕГО ФИЛИАЛА. Смена во Владивостоке и смена в Москве в один
 * календарный день начинаются в разное абсолютное время, и складывать их в одну строку
 * по UTC значит показать мастеру чужой день.
 */

export interface MasterWeekShift {
  /** «10:00–20:00» — как мастер называет свою смену вслух. */
  readonly hours: string
  readonly branchId: string
  readonly branchName: string
  readonly workMinutes: number
  readonly busyMinutes: number
}

export interface MasterWeekDay {
  readonly date: string
  /** «Пн 15» — день недели и число, как в макете. */
  readonly label: string
  readonly isToday: boolean
  /** Смены этого дня. Пусто — либо выходной, либо смену снимали, а записи оставили. */
  readonly shifts: readonly MasterWeekShift[]
  /** Все живые записи дня: и попавшие в смену, и оставшиеся вне её. */
  readonly appointments: number
  /**
   * Записи вне всех смен дня.
   *
   * ЭТО НЕ АНОМАЛИЯ, А ШТАТНОЕ СОСТОЯНИЕ. Сервер предупреждает «в это время у мастера
   * нет смены», администратор подтверждает — и запись создаётся; правка графика с
   * «оставить визиты» тоже намеренно оставляет клиентов вне новой смены. Промолчать
   * о них значит отправить мастера домой в день, когда его ждут четыре человека.
   */
  readonly outsideAppointments: number
  /** Точки, в которые ведёт день: филиалы со сменой или с записью. */
  readonly targets: readonly { readonly branchId: string; readonly branchName: string }[]
  readonly workMinutes: number
  readonly busyMinutes: number
  /** Доля занятого времени смен, 0…100. Для полосы загрузки. */
  readonly percent: number
  /** Название праздника из производственного календаря. null — обычный день. */
  readonly holiday: string | null
  /** Предпраздничный сокращённый день: работать короче — это тоже новость. */
  readonly isShortDay: boolean
}

export interface MasterWeekView {
  readonly days: readonly MasterWeekDay[]
  readonly shiftCount: number
  readonly appointments: number
  readonly freeMinutes: number
  /** Ни одной смены и ни одной записи: это ответ, а не пустота. */
  readonly noShifts: boolean
  /** Смены есть, а записей нет: тоже ответ, и другой. */
  readonly noAppointments: boolean
  /**
   * Смен нет, а записи есть — самое опасное состояние недели, и оно не «пусто».
   * Мастер, увидевший «смен нет», не придёт; клиенты придут.
   */
  readonly bookedWithoutShift: boolean
  /**
   * Неделя собрана НЕ ИЗ ВСЕХ точек: какой-то филиал не ответил.
   *
   * ПРИ НЕПОЛНОЙ НЕДЕЛЕ УТВЕРЖДАТЬ НЕЛЬЗЯ НИЧЕГО. «Смен нет» означает «вопрос
   * к администратору», и сказать это из-за таймаута одной точки значит послать
   * мастера выяснять то, чего не было.
   */
  readonly partial: boolean
}

const WEEKDAY_LABELS: readonly string[] = ["Вс", "Пн", "Вт", "Ср", "Чт", "Пт", "Сб"]

/** «Пн 15» — короткая подпись дня для узкой строки. */
export function weekDayLabel(date: string): string {
  const parsed = new Date(`${date}T12:00:00Z`)
  const weekday = WEEKDAY_LABELS[parsed.getUTCDay()] ?? ""
  return `${weekday} ${String(parsed.getUTCDate())}`
}

/** «10:00–20:00». Время смены — в зоне её филиала, а не устройства. */
function shiftHours(startAt: string, endAt: string, timeZone: string): string {
  return `${formatTimeOfDay(startAt, timeZone)}–${formatTimeOfDay(endAt, timeZone)}`
}

function minutesBetween(startAt: string, endAt: string): number {
  const from = new Date(startAt).getTime()
  const to = new Date(endAt).getTime()
  return Number.isFinite(from) && Number.isFinite(to) ? Math.max(0, Math.round((to - from) / 60_000)) : 0
}

interface BranchDay {
  readonly shifts: MasterWeekShift[]
  /** Записи, легшие в смену этого дня. */
  appointments: number
  /** Записи этого дня, не попавшие ни в одну смену. */
  outside: number
}

/**
 * Один ответ журнала по одному филиалу за неделю → дни этого филиала.
 *
 * ЗАНЯТОСТЬ СЧИТАЕТСЯ ПО ЗАПИСЯМ, а не по `busy`: у мастера в ответе только его
 * собственные строки, и запись — это ровно то время, которое у него продано. Перерыв
 * и уборка в «занято» не идут: мастер спрашивает «сколько я работаю», а не «сколько
 * я занят чем-нибудь».
 *
 * ОТМЕНЁННОЕ НЕ СЧИТАЕТСЯ. Отменённая запись освободила время, и показывать её
 * в загрузке значит советовать мастеру не ждать клиентов там, где он свободен.
 *
 * ДЕНЬ СТРОИТСЯ ИЗ СМЕН И ЗАПИСЕЙ, А НЕ ТОЛЬКО ИЗ СМЕН. Обход одних смен терял день,
 * в котором смену сняли, а клиентов оставили: такой день выходил «выходным» при четырёх
 * записях в нём.
 *
 * ЗАПИСЬ ЛОЖИТСЯ В ДЕНЬ СВОЕЙ СМЕНЫ, А НЕ СВОИХ СУТОК. Ночная смена 22:00–06:00 —
 * поддерживаемый случай, и записи с 00:30 до 05:00 принадлежат вечеру воскресенья,
 * а не утру понедельника: мастеру они нужны в той строке, в которой он на работу
 * выходит. Календарная дата используется только для записей, не попавших ни в смену.
 *
 * ЗАНЯТОЕ ВРЕМЯ ОБРЕЗАЕТСЯ ОКНОМ СМЕНЫ. Запись 13:30–16:00 при смене до 14:00 занимает
 * тридцать минут смены, а не сто пятьдесят: иначе «свободно 0 мин» появлялось у мастера,
 * у которого посреди смены свободны полтора часа, и он отказывал клиенту, которого мог
 * взять.
 */
function daysOfBranch(result: ScheduleResult, branchName: string): Map<string, BranchDay> {
  const zone = result.branch.timezone
  const windows = result.workdays.map((workday) => ({
    date: workday.date,
    from: new Date(workday.startAt).getTime(),
    to: new Date(workday.endAt).getTime(),
    hours: shiftHours(workday.startAt, workday.endAt, zone),
    workMinutes: minutesBetween(workday.startAt, workday.endAt),
    busyMs: 0,
  }))

  const counts = new Map<string, number>()
  const outside = new Map<string, number>()

  for (const appointment of result.appointments) {
    if (appointment.status === "cancelled") continue
    const from = new Date(appointment.startAt).getTime()
    const to = new Date(appointment.endAt).getTime()
    if (!Number.isFinite(from) || !Number.isFinite(to)) continue

    let bestDate: string | null = null
    let bestOverlap = 0
    for (const window of windows) {
      const overlap = Math.max(0, Math.min(to, window.to) - Math.max(from, window.from))
      if (overlap === 0) continue
      window.busyMs += overlap
      if (overlap > bestOverlap) {
        bestOverlap = overlap
        bestDate = window.date
      }
    }
    if (bestDate === null) {
      // Запись вне всех смен — по календарной дате филиала: другого дня у неё нет.
      const date = todayInZone(zone, new Date(from))
      outside.set(date, (outside.get(date) ?? 0) + 1)
    } else {
      counts.set(bestDate, (counts.get(bestDate) ?? 0) + 1)
    }
  }

  const out = new Map<string, BranchDay>()
  const row = (date: string): BranchDay => {
    const found = out.get(date)
    if (found !== undefined) return found
    const fresh: BranchDay = { shifts: [], appointments: 0, outside: 0 }
    out.set(date, fresh)
    return fresh
  }

  for (const window of windows) {
    row(window.date).shifts.push({
      hours: window.hours,
      branchId: result.branch.id,
      branchName,
      workMinutes: window.workMinutes,
      // Наложение двух записей друг на друга могло бы дать «занято больше смены»:
      // полоса загрузки такое не нарисует, а «свободно −20 мин» не имеет смысла.
      busyMinutes: Math.min(window.workMinutes, Math.round(window.busyMs / 60_000)),
    })
  }
  for (const [date, count] of counts) row(date).appointments += count
  for (const [date, count] of outside) row(date).outside += count
  return out
}

/**
 * Неделя мастера из ответов по всем его филиалам.
 *
 * `dates` задаются снаружи, а не выводятся из ответов: неделя обязана показать все семь
 * дней, включая те, где нет ни смены, ни записи. Выходной — это ответ, а не отсутствие
 * строки, и пропущенный день читался бы как сбой загрузки.
 */
export function buildMasterWeek(input: {
  readonly dates: readonly string[]
  readonly today: string
  readonly results: readonly { readonly result: ScheduleResult; readonly branchName: string }[]
  /** Хотя бы одна точка не ответила: неделя неполная, и утверждать по ней нельзя. */
  readonly partial?: boolean
}): MasterWeekView {
  const byBranch = input.results.map((entry) => ({
    branchId: entry.result.branch.id,
    branchName: entry.branchName,
    days: daysOfBranch(entry.result, entry.branchName),
  }))

  // Подсказка календаря — общая для всех филиалов: праздник в стране один. Берём
  // из первого ответа, где она есть: спрашивать её отдельно незачем.
  const hints = new Map<string, { title: string | null; dayType: string }>()
  for (const entry of input.results) {
    for (const day of entry.result.days) {
      if (day.calendarHint !== null && !hints.has(day.date)) {
        hints.set(day.date, { title: day.calendarHint.title, dayType: day.calendarHint.dayType })
      }
    }
  }

  const days: MasterWeekDay[] = input.dates.map((date) => {
    const shifts: MasterWeekShift[] = []
    const targets: { branchId: string; branchName: string }[] = []
    let appointments = 0
    let outsideAppointments = 0
    for (const branch of byBranch) {
      const row = branch.days.get(date)
      if (row === undefined) continue
      shifts.push(...row.shifts)
      appointments += row.appointments + row.outside
      outsideAppointments += row.outside
      // Точка попадает в цели дня и когда смены в ней нет, а запись есть: иначе
      // такой день открывался бы в чужом филиале, где мастера в этот день не ждут.
      if (row.shifts.length > 0 || row.outside > 0) {
        targets.push({ branchId: branch.branchId, branchName: branch.branchName })
      }
    }
    const workMinutes = shifts.reduce((sum, shift) => sum + shift.workMinutes, 0)
    const busyMinutes = shifts.reduce((sum, shift) => sum + shift.busyMinutes, 0)
    const hint = hints.get(date) ?? null

    return {
      date,
      label: weekDayLabel(date),
      isToday: date === input.today,
      shifts,
      appointments,
      outsideAppointments,
      targets,
      workMinutes,
      busyMinutes,
      // Процент от нуля не считается, а не равен нулю: в выходной полосы нет вовсе.
      percent: workMinutes > 0 ? Math.min(100, Math.round((busyMinutes / workMinutes) * 100)) : 0,
      holiday: hint === null ? null : hint.dayType === "short" ? null : hint.title ?? "праздник",
      isShortDay: hint?.dayType === "short",
    }
  })

  const shiftCount = days.reduce((sum, day) => sum + day.shifts.length, 0)
  const appointments = days.reduce((sum, day) => sum + day.appointments, 0)
  const workMinutes = days.reduce((sum, day) => sum + day.workMinutes, 0)
  const busyMinutes = days.reduce((sum, day) => sum + day.busyMinutes, 0)

  const partial = input.partial ?? false

  return {
    days,
    shiftCount,
    appointments,
    freeMinutes: Math.max(0, workMinutes - busyMinutes),
    // Ни одного утверждения при неполной неделе: молчание честнее приговора,
    // вынесенного по таймауту одной точки.
    noShifts: !partial && shiftCount === 0 && appointments === 0,
    noAppointments: !partial && shiftCount > 0 && appointments === 0,
    bookedWithoutShift: shiftCount === 0 && appointments > 0,
    partial,
  }
}

/** «9 ч 05 мин», «40 мин». Часы салона читаются часами. */
export function hoursLabel(minutes: number): string {
  const safe = Math.max(0, Math.round(minutes))
  const hours = Math.floor(safe / 60)
  const rest = safe % 60
  if (hours === 0) return `${String(rest)} мин`
  if (rest === 0) return `${String(hours)} ч`
  return `${String(hours)} ч ${String(rest).padStart(2, "0")} мин`
}

/**
 * Подпись недели под заголовком: «4 смены · 29 записей · свободно 11 ч».
 *
 * ТРИ СОСТОЯНИЯ ГОВОРЯТ РАЗНОЕ, и путать их нельзя. «Смен нет» — вопрос
 * к администратору. «Смены есть, записей нет» — вопрос к самому себе и к салону:
 * время продаётся, а не продано. Общая же строка — просто сводка.
 */
export function weekSummary(view: MasterWeekView): string {
  // «Смен нет, а записи есть» сильнее всего остального: это не сводка, а тревога.
  if (view.bookedWithoutShift) return "смен нет, а записи есть — спросите администратора"
  if (view.partial && view.shiftCount === 0) return "неделя загружена не целиком"
  if (view.noShifts) return "на этой неделе смен нет"
  if (view.noAppointments) {
    return `смены есть, записей нет · свободно ${hoursLabel(view.freeMinutes)}`
  }
  const shifts = `${String(view.shiftCount)} ${plural(view.shiftCount, "смена", "смены", "смен")}`
  const records = `${String(view.appointments)} ${plural(view.appointments, "запись", "записи", "записей")}`
  return `${shifts} · ${records} · свободно ${hoursLabel(view.freeMinutes)}`
}

/**
 * Подпись дня: «смена 10–20 · сегодня», «выходной · праздник по календарю».
 *
 * ФИЛИАЛ ДОПИСЫВАЕТСЯ ТОЛЬКО В СЕТИ. У мастера одной точки «на Ленина» в каждой строке
 * — шум: он и так знает, где работает. У мастера двух точек это главное слово в строке.
 */
export function dayLine(day: MasterWeekDay, network: boolean, partial = false): string {
  const parts: string[] = []
  if (day.shifts.length === 0) {
    /*
     * ТРИ РАЗНЫЕ ПУСТОТЫ, И ПУТАТЬ ИХ НЕЛЬЗЯ. «Выходной» — можно не приходить.
     * «Смены нет, а записи есть» — приходить обязательно, и это разговор
     * с администратором. «Смен в загруженных точках нет» — мы просто не знаем,
     * потому что филиал не ответил.
     */
    if (day.appointments > 0) parts.push("смены нет, а записи есть")
    else if (partial) parts.push("смен в загруженных точках нет")
    else parts.push("выходной")
  } else {
    for (const shift of day.shifts) {
      parts.push(network ? `смена ${shift.hours} · на ${shift.branchName}` : `смена ${shift.hours}`)
    }
  }
  if (day.isToday) parts.push("сегодня")
  if (day.holiday !== null) {
    // Название праздника, а не просто «праздник»: «День народного единства» объясняет,
    // почему салон может быть закрыт, а безымянная пометка — нет.
    parts.push(`праздник по календарю: ${day.holiday}`)
  }
  /*
   * «КОРОЧЕ НА ЧАС» НЕ УТВЕРЖДАЕТСЯ. Календарь — только подсказка, а часы ставит салон,
   * и они стоят в этой же строке: приписка «короче на час» рядом со сменой 10:00–20:00
   * отправляла мастера домой в 19:00 к записанному на 19:15. В выходной её нет вовсе —
   * короче на час относительно ничего.
   */
  if (day.isShortDay && day.shifts.length > 0) parts.push("предпраздничный день")
  return parts.join(" · ")
}

/** Детали дня: «7 записей · свободно 3 ч». В выходной деталей нет. */
export function dayDetail(day: MasterWeekDay): string | null {
  const records = `${String(day.appointments)} ${plural(day.appointments, "запись", "записи", "записей")}`
  if (day.shifts.length === 0) {
    // День без смены, но с клиентами: «свободно» тут не считается — считать нечего,
    // а число записей — ровно то, зачем мастеру эта строка.
    return day.appointments === 0 ? null : `${records} · смены нет, но вас ждут`
  }
  const free = Math.max(0, day.workMinutes - day.busyMinutes)
  const base =
    day.appointments === 0
      ? `записей нет · свободно ${hoursLabel(free)}`
      : `${records} · свободно ${hoursLabel(free)}`
  // Записи вне смены объясняют, почему «свободно» и число записей не сходятся.
  return day.outsideAppointments === 0
    ? base
    : `${base} · из них вне смены ${String(day.outsideAppointments)}`
}

/** Порог, за которым день подписывается «плотно» (макет 5-М2). */
export const TIGHT_PERCENT = 85

/**
 * Чип справа в строке дня: «сегодня» или «плотно». null — чипа нет.
 *
 * «Плотно» — это предупреждение, а не украшение: день, забитый на девять десятых,
 * не выдержит ни опоздания, ни разговора с клиентом дольше обычного.
 */
export function dayTag(day: MasterWeekDay): "today" | "tight" | null {
  if (day.isToday) return "today"
  if (day.shifts.length > 0 && day.percent >= TIGHT_PERCENT) return "tight"
  // День без смены с записями — тоже «плотно»: прийти придётся, и это главное.
  if (day.shifts.length === 0 && day.appointments > 0) return "tight"
  return null
}

/** «График ставит администратор» — почему у мастера нет кнопки правки (ТЗ §5.4). */
export const SCHEDULE_OWNER_NOTE =
  "График ставит администратор. Нужно поменяться или отпроситься — скажите ему: из этого экрана график не меняется."
